import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Search, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Eye, Pencil, Save, X, Loader2, Filter } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

const COLUMNS = [
  { key: 'first_name', label: 'Name', sortable: true },
  { key: 'surname', label: 'Surname', sortable: true },
  { key: 'age', label: 'Age', sortable: true },
  { key: 'union', label: 'Union', sortable: true },
  { key: 'province', label: 'Province', sortable: true },
  { key: 'mobile', label: 'Mobile', sortable: true },
  { key: 'email', label: 'Email', sortable: true },
];

export const AdminMemberList = () => {
  const { adminApi } = useAuth();
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(null);

  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('first_name');
  const [sortDir, setSortDir] = useState('asc');
  const [provinceFilter, setProvinceFilter] = useState('');
  const [unionFilter, setUnionFilter] = useState('');

  const [viewMember, setViewMember] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search,
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      if (provinceFilter) params.set('province', provinceFilter);
      if (unionFilter) params.set('union', unionFilter);

      const res = await adminApi.get(`/admin/union-members?${params}`);
      setMembers(res.data.members);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, sortBy, sortDir, provinceFilter, unionFilter, adminApi]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const res = await adminApi.get('/admin/union-members/filters');
        setFilters(res.data);
      } catch (e) { console.error(e); }
    };
    loadFilters();
  }, [adminApi]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
    setPage(1);
  };

  const handleEdit = (member) => {
    setEditId(member._id);
    setEditData({ ...member });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/admin/union-members/${editId}`, editData);
      toast.success('Member updated');
      setEditId(null);
      fetchMembers();
    } catch (err) {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleView = async (member) => {
    try {
      const res = await adminApi.get(`/admin/union-members/${member._id}`);
      setViewMember(res.data);
    } catch (e) {
      toast.error('Failed to load member');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ChevronUp className="w-3 h-3 text-gray-300" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 text-afriperx-green" />
      : <ChevronDown className="w-3 h-3 text-afriperx-green" />;
  };

  const startRecord = (page - 1) * limit + 1;
  const endRecord = Math.min(page * limit, total);

  return (
    <div className="space-y-4" data-testid="admin-member-list">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="font-syne text-2xl font-bold text-gray-900">All Members</h1>
          <p className="text-gray-400 text-sm">{total.toLocaleString()} total members</p>
        </div>
      </div>

      {/* Search + Filters */}
      <Card className="p-4 rounded-xl border border-gray-100 shadow-sm">
        <div className="flex flex-col md:flex-row gap-3">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Search name, surname, mobile, email..."
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:border-afriperx-green focus:ring-1 focus:ring-afriperx-green/20 outline-none text-sm"
                data-testid="member-search-input"
              />
            </div>
            <button type="submit" className="px-4 py-2.5 bg-afriperx-green text-white rounded-lg text-sm font-medium hover:bg-afriperx-green-dark transition-colors" data-testid="member-search-btn">
              Search
            </button>
          </form>
          <div className="flex gap-2">
            <select
              value={provinceFilter}
              onChange={e => { setProvinceFilter(e.target.value); setPage(1); }}
              className="px-3 py-2.5 rounded-lg border border-gray-200 text-sm bg-white focus:border-afriperx-green outline-none"
              data-testid="province-filter"
            >
              <option value="">All Provinces</option>
              {filters?.provinces?.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select
              value={unionFilter}
              onChange={e => { setUnionFilter(e.target.value); setPage(1); }}
              className="px-3 py-2.5 rounded-lg border border-gray-200 text-sm bg-white focus:border-afriperx-green outline-none"
              data-testid="union-filter"
            >
              <option value="">All Unions</option>
              {filters?.unions?.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="members-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {COLUMNS.map(col => (
                  <th
                    key={col.key}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                    onClick={() => col.sortable && handleSort(col.key)}
                    data-testid={`sort-${col.key}`}
                  >
                    <div className="flex items-center gap-1">
                      {col.label}
                      {col.sortable && <SortIcon col={col.key} />}
                    </div>
                  </th>
                ))}
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center">
                  <Loader2 className="w-6 h-6 animate-spin text-afriperx-green mx-auto" />
                </td></tr>
              ) : members.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400 text-sm">No members found</td></tr>
              ) : members.map(member => (
                <tr key={member._id} className="hover:bg-gray-50/50 transition-colors" data-testid={`member-row-${member._id}`}>
                  {editId === member._id ? (
                    <>
                      {COLUMNS.map(col => (
                        <td key={col.key} className="px-3 py-2">
                          <input
                            value={editData[col.key] || ''}
                            onChange={e => setEditData(prev => ({ ...prev, [col.key]: e.target.value }))}
                            className="w-full px-2 py-1.5 text-sm border border-afriperx-green/30 rounded-md focus:border-afriperx-green outline-none bg-white"
                            data-testid={`edit-${col.key}`}
                          />
                        </td>
                      ))}
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={handleSave} disabled={saving} className="p-1.5 rounded-md bg-afriperx-green text-white hover:bg-afriperx-green-dark transition-colors" data-testid="save-member-btn">
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          </button>
                          <button onClick={() => setEditId(null)} className="p-1.5 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors" data-testid="cancel-edit-btn">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">{member.first_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{member.surname}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{member.age}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-50 text-emerald-700 font-medium">{member.union}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 text-xs rounded-full bg-sky-50 text-sky-700 font-medium">{member.province}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{member.mobile}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 truncate max-w-[180px]">{member.email}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleView(member)} className="p-1.5 rounded-md text-gray-400 hover:text-afriperx-green hover:bg-green-50 transition-colors" title="View" data-testid={`view-${member._id}`}>
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleEdit(member)} className="p-1.5 rounded-md text-gray-400 hover:text-afriperx-gold hover:bg-amber-50 transition-colors" title="Edit" data-testid={`edit-${member._id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
          <p className="text-xs text-gray-500">
            Showing <span className="font-medium text-gray-700">{startRecord.toLocaleString()}</span> to <span className="font-medium text-gray-700">{endRecord.toLocaleString()}</span> of <span className="font-medium text-gray-700">{total.toLocaleString()}</span>
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={page <= 1}
              className="px-2 py-1.5 text-xs rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="page-first"
            >First</button>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="page-prev"
            ><ChevronLeft className="w-4 h-4" /></button>
            <span className="px-3 py-1.5 text-xs text-gray-700 font-medium">
              Page {page.toLocaleString()} of {pages.toLocaleString()}
            </span>
            <button
              onClick={() => setPage(p => Math.min(pages, p + 1))}
              disabled={page >= pages}
              className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="page-next"
            ><ChevronRight className="w-4 h-4" /></button>
            <button
              onClick={() => setPage(pages)}
              disabled={page >= pages}
              className="px-2 py-1.5 text-xs rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="page-last"
            >Last</button>
          </div>
        </div>
      </Card>

      {/* View Modal */}
      {viewMember && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setViewMember(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="view-member-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-syne font-bold text-gray-900">Member Details</h3>
              <button onClick={() => setViewMember(null)} className="p-1 rounded-md hover:bg-gray-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              {[
                { label: 'Name', value: `${viewMember.first_name} ${viewMember.surname}` },
                { label: 'Age', value: viewMember.age },
                { label: 'Mobile', value: viewMember.mobile },
                { label: 'Email', value: viewMember.email },
                { label: 'Province', value: viewMember.province },
                { label: 'Union', value: viewMember.union },
                { label: 'Record ID', value: viewMember.record_id },
              ].map((f, i) => (
                <div key={i} className="flex justify-between py-2 border-b border-gray-50 last:border-0">
                  <span className="text-gray-400 text-sm">{f.label}</span>
                  <span className="text-gray-900 text-sm font-medium text-right">{f.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
